import debug from "debug";
import invariant from "tiny-invariant";

import type { StructuredLogger } from "./structuredLogger.ts";
import structuredLogger from "./structuredLogger.ts";

if (typeof window !== "undefined") {
  invariant(process.env.NEXT_PUBLIC_DEBUG, "NEXT_PUBLIC_DEBUG is not set");
  debug.enable(process.env.NEXT_PUBLIC_DEBUG);
}

const debugBase = debug("ai");
// By default debug logs to stderr, we want to use stdout
debugBase.log = console.log.bind(console);

export type LoggerKey =
  | "teaching-materials"
  | "teaching-materials:testing"
  | "teaching-materials-threat-detection"
  | "admin"
  | "aila"
  | "aila:agents"
  | "aila:agents:prompts"
  | "aila:agents:stream"
  | "aila:analytics"
  | "aila:categorisation"
  | "aila:chat"
  | "aila:errors"
  | "aila:experimental-patches"
  | "aila:lesson"
  | "aila:llm"
  | "aila:moderation"
  | "aila:moderation:response"
  | "aila:persistence"
  | "aila:prompt"
  | "aila:protocol"
  | "aila:quiz"
  | "aila:quiz:reporting"
  | "aila:rag"
  | "aila:schema"
  | "aila:stream"
  | "aila:fixtures"
  | "aila:testing"
  | "aila:threat"
  | "adaptations"
  | "analytics"
  | "analytics:lesson:store"
  | "app"
  | "appSessions"
  | "auth"
  | "chat"
  | "chat:store"
  | "cloudinary"
  | "consent"
  | "core"
  | "cron"
  | "db"
  | "demo"
  | "exports"
  | "feature-flags"
  | "feedback"
  | "fixtures"
  | "generation"
  | "ingest"
  | "judgements"
  | "lakera-client"
  | "lessons"
  | "lessons:scrolling"
  | "lessons:store"
  | "middleware:auth"
  | "moderation"
  | "moderation:store"
  | "posthog-surveys"
  | "prompts"
  | "qd"
  | "quiz"
  | "rag"
  | "rate-limiting"
  | "scrolling"
  | "snippets"
  | "testing"
  | "tracing"
  | "transcripts"
  | "trpc"
  | "ui"
  | "ui:performance"
  | "webhooks";

/**
 * The AI logger uses namespaces so that we can selectively toggle noisy logs.
 * Logs are selected with the DEBUG environment variable.
 * Error logs are always shown.
 *
 * @example Include all logs except prisma
 * DEBUG=ai:*,-ai:db
 *
 * @example Usage in a module
 * import { aiLogger } from "@ai-jsx/logger";
 *
 * const log = aiLogger("db");
 * log.info("Hello world");
 */
export function aiLogger(childKey: LoggerKey) {
  const debugLogger = debugBase.extend(childKey);

  const tableLogger = (tabularData: unknown[], columns?: string[]) => {
    if (typeof console !== "undefined" && console.table) {
      console.table(tabularData, columns);
    }
  };
  return {
    info: debugLogger,
    warn: debugLogger,
    error: console.error,
    table: tableLogger,
  };
}

export { structuredLogger };
export type { StructuredLogger };
